import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Select, Row, Col, Form } from "antd";
import {
  LabelWrapper,
  InputWrapper,
  FormContainer,
  GrantDropdown,
  SelectOptionWrapper,
  SelectDropdown,
  UnderlyingContent,
  ErrorMsgWrap,
  GrantDatePicker,
  InputSection
} from "../../style";
import ScheduleRecurringPopup from "../ScheduleRecurringPopup/ScheduleRecurringPopup";
import FormBase from "../../../../../../CommonComponents/FormBase/FormBase";
import regexMap from "../../../../../../utils/regexMap";
import formatter from "../../../../../../utils/formatter";
import {
  calEndDate,
  validationRuleStateChecker,
  messageStateChecker
} from "../../utils";
import CurrencyInput from "../../../../../../CommonComponents/CurrencyInput/CurrencyInput";
import validate from "../../../../../../utils/validations";
import DrupalPopup from "../Popup/DrupalPopup";

class RecurringGrantsForm extends FormBase {
  constructor(props) {
    super(props);
    const lowBalCheck = parseFloat(props.availableBalance) < parseFloat(500);
    // if available balance is less than $500, make available balance min grant amount
    const minGrantAmount = 500;
    const minNoOfPayments = 2;
    this.state = {
      showFrequencyConfirm: false,
      frequencyTempVal: "",
      frequencyTempType: "",
      showGrantSchedule: false,
      editMode: props.editMode,
      status: props.status,
      amount: {
        value: props.amount || "",
        valid: "",
        touched: false,
        disabled: props.amountLocked || false,
        matchPattern: regexMap.twoDecimal,
        validationRules: {
          isRequired: true,
          lessThanValue: props.availableBalance,
          greaterThanValue: !lowBalCheck && minGrantAmount
        },
        messages: {
          isRequired: "Please enter grant amount.",
          lessThanValue:
            "The grant amount entered exceeds the account balance. Please enter a value less than the account balance.",
          greaterThanValue: "Enter a minimum of $500."
        }
      },
      date: {
        value: props.date || "",
        valid: "",
        touched: false,
        validationRules: {
          isRequired: true
        },
        messages: {
          isRequired: "Please enter start date"
        }
      },
      frequency: {
        value: props.frequency || "",
        valid: "",
        touched: false,
        validationRules: {
          isRequired: true
        },
        messages: {
          isRequired: "Please select frequency"
        }
      },
      noOfPayment: {
        value: props.noOfPayment || "",
        valid: "",
        touched: false,
        matchPattern: regexMap.numbers,
        validationRules: {
          isRequired: true,
          greaterThanValue: minNoOfPayments
        },
        messages: {
          isRequired: "Please enter number of payments.",
          greaterThanValue: "Please enter at least two payments."
        }
      }
    };
    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.checkFormValid = this.checkFormValid.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (
      newProps.amount !== this.props.amount ||
      newProps.amountLocked !== this.props.amountLocked
    ) {
      this.setState(prevState => ({
        amount: {
          ...prevState.amount,
          value: newProps.amount,
          disabled: newProps.amountLocked
        }
      }));
    }
    if(newProps.individualValidator !== this.props.individualValidator){
      this.runFormValidation();
    }
  };

  formatData = () => ({
    data: {
      grantAmount: this.state.amount.value,
      startDate: this.state.date.value,
      frequency: this.state.frequency.value,
      noOfPayments: this.state.noOfPayment.value
    },
    isFormValid: this.checkFormValid(this.state)
  });

  calTotalAmount = (grantAmount, noOfPayment) => {
    return grantAmount * noOfPayment;
  };

  disabledDate = current => current && current < moment().subtract(1, "days");

  startDateChange = (date, dateString) => {
    this.setState(
      prevState => ({
        date: { ...prevState.date, value: dateString, touched: true }
      }),
      this.handleFormChange
    );
  };

  viewGrantSchedulePopup = () => {
    this.setState({
      showGrantSchedule: !this.state.showGrantSchedule
    });
  };

  frequencyChangeHandler = (value, type) => {
    if (this.state.editMode) {
      this.setState({
        showFrequencyConfirm: true,
        frequencyTempVal: value,
        frequencyTempType: type
      });
    } else {
      this.onChangeHandler(value, type);
    }
  };

  confirmFrequencyTypeChange = () => {
    this.onChangeHandler(
      this.state.frequencyTempVal,
      this.state.frequencyTempType
    );
    this.setState({
      showFrequencyConfirm: false,
      frequencyTempVal: "",
      frequencyTempType: ""
    });
  };

  cancelFrequencyHandler = () => {
    this.setState({
      showFrequencyConfirm: false,
      frequencyTempVal: "",
      frequencyTempType: ""
    });
  };

  onChangeHandler = (value, type) => {
    this.setState(
      prevState => ({
        noOfPayment: {
          ...prevState.noOfPayment,
          validationRules: {
            ...prevState.noOfPayment.validationRules,
            ...validationRuleStateChecker(value)
          },
          messages: {
            ...prevState.noOfPayment.messages,
            ...messageStateChecker(value)
          },
          valid:
            prevState.noOfPayment.value &&
            validate(
              prevState.noOfPayment.value,
              validationRuleStateChecker(value)
            )
        }
      }),
      () => {
        this.handleSelectChange(value, type);
      }
    );
  };

  render() {
    const {
      amount,
      date,
      frequency,
      noOfPayment,
      showGrantSchedule,
      editMode,
      status,
      showFrequencyConfirm
    } = this.state;
    let endDate, totalAmount;
    if (amount.value && noOfPayment.value) {
      totalAmount = this.calTotalAmount(amount.value, noOfPayment.value);
    }
    if (date.value && frequency.value && noOfPayment.value) {
      endDate = calEndDate(date.value, noOfPayment.value, frequency.value);
    }
    return (
      <div>
        <FormContainer>
          <Row>
            <Col lg={8} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>Frequency</LabelWrapper>}
              >
                <InputWrapper>
                  <GrantDropdown
                    name="frequency"
                    id="selfrequency"
                    value={
                      frequency.value ? (
                        frequency.value
                      ) : (
                        <SelectDropdown>- Select -</SelectDropdown>
                      )
                    }
                    onChange={val =>
                      this.frequencyChangeHandler(val, "frequency")
                    }
                    aria-label="select"
                    role={false}
                  >
                    <Select.Option value="Monthly">
                      <SelectOptionWrapper>Monthly</SelectOptionWrapper>
                    </Select.Option>
                    <Select.Option value="Quarterly">
                      <SelectOptionWrapper>Quarterly</SelectOptionWrapper>
                    </Select.Option>
                    <Select.Option value="Semiannually">
                      <SelectOptionWrapper>Semiannually</SelectOptionWrapper>
                    </Select.Option>
                    <Select.Option value="Annually">
                      <SelectOptionWrapper>Annually</SelectOptionWrapper>
                    </Select.Option>
                  </GrantDropdown>
                  {frequency.valid && (
                    <ErrorMsgWrap>
                      {frequency.messages[frequency.valid]}
                    </ErrorMsgWrap>
                  )}
                </InputWrapper>
              </Form.Item>
            </Col>
            <Col lg={{ span: 8, offset: 2 }} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>Grant Amount</LabelWrapper>}
              >
                <InputWrapper>
                  <CurrencyInput
                    focus={false}
                    name="amount"
                    id="txtamount"
                    label="grant-amount"
                    aria-label="Grant Amounts"
                    placeholder="Enter grant amount"
                    maxLength={150}
                    value={amount.value}
                    onChange={this.handleInputChange}
                    onBlur={this.handleBlur}
                    disabled={amount.disabled}
                  />
                  {amount.valid && (
                    <ErrorMsgWrap>{amount.messages[amount.valid]}</ErrorMsgWrap>
                  )}
                </InputWrapper>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col lg={8} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>Start Date</LabelWrapper>}
              >
                <InputWrapper>
                <GrantDatePicker
                    disabled={editMode && status === "Active"}
                    name="date"
                    id="txtdate"
                    aria-label="date-select"
                    placeholder="Select start date"
                    format="MM/DD/YYYY"
                    disabledDate={this.disabledDate}
                    onChange={this.startDateChange}
                    defaultValue={date.value ? moment(date.value) : null}
                  />
                  {date.valid && (
                    <ErrorMsgWrap>
                      {date.messages[date.valid]}
                    </ErrorMsgWrap>
                  )}
                </InputWrapper>
              </Form.Item>
            </Col>
            <Col lg={{ span: 8, offset: 2 }} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>Number of payments</LabelWrapper>}
              >
                <InputWrapper>
                  <InputSection
                    disabled={editMode && status === "Active"}
                    name="noOfPayment"
                    id="txtnoOfPayment"
                    placeholder="Enter number of payments"
                    aria-label="no-of-payment"
                    type="number"
                    min="0"
                    value={noOfPayment.value}
                    onChange={this.handleInputChange}
                    onBlur={this.handleBlur}
                  />
                  {noOfPayment.valid && (
                    <ErrorMsgWrap>
                      {noOfPayment.messages[noOfPayment.valid]}
                    </ErrorMsgWrap>
                  )}
                </InputWrapper>
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" align="middle">
            <Col lg={8} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>End Date</LabelWrapper>}
              >
                <InputWrapper>
                  <InputSection placeholder="End Date" aria-label="end-date" id="txtendDate" disabled value={endDate} />
                </InputWrapper>
              </Form.Item>
            </Col>
            <Col lg={{ span: 8, offset: 2 }} span={24}>
              <Form.Item
                colon={false}
                label={<LabelWrapper>Total Amount</LabelWrapper>}
              >
                <InputWrapper>
                  <InputSection
                    id="txtTotalAmount"
                    placeholder="Total Amount"
                    type="text"
                    aria-label="total-amount"
                    value={formatter({
                      value: totalAmount,
                      fieldSuffix: "Dollars"
                    })}
                    disabled
                  />
                </InputWrapper>
              </Form.Item>
            </Col>
            <Col lg={{ span: 5, offset: 1 }} span={24}>
              <UnderlyingContent id="anrViewgrantschedule" onClick={this.viewGrantSchedulePopup}>
                View grant schedule
              </UnderlyingContent>
            </Col>
          </Row>
        </FormContainer>
        {showGrantSchedule && (
          <ScheduleRecurringPopup
            grantName={this.props.grantName}
            startDate={date.value}
            frequency={frequency.value}
            noOfPayment={noOfPayment.value}
            amount={amount.value}
            closeToggle={this.viewGrantSchedulePopup}
          />
        )}
        <DrupalPopup
          html={true}
          page={"Edit Grant"}
          show={showFrequencyConfirm}
          defaultPrimaryLabel={"Ok"}
          defaultTitle={""}
          defaultSecondaryLabel={"Cancel"}
          keyProperty={"Frequency Change Edit Grant"}
          defaultDescription={
            "By changing the frequency of your recurring grant you are deleting the information below for all future payment grants and will be replacing it with new information"
          }
          primaryCta={this.confirmFrequencyTypeChange}
          secondaryCta={this.cancelFrequencyHandler}
          alertId='freq-change'
        />
      </div>
    );
  }
}

RecurringGrantsForm.propTypes = {
  amount: PropTypes.number,
  amountLocked: PropTypes.bool,
  availableBalance: PropTypes.string,
  date: PropTypes.string,
  editMode: PropTypes.bool,
  frequency: PropTypes.string,
  grantName: PropTypes.string,
  handleFormChange: PropTypes.func,
  individualValidator: PropTypes.bool,
  noOfPayment: PropTypes.string,
  status: PropTypes.string,
}

export default RecurringGrantsForm;
